import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { AccessLevel, AccessOverlay } from '../../shared/index.js';
import {
  ACCESS_LEVEL_LABELS,
  ACCESS_OVERLAYS,
  ACCESS_OVERLAY_LABELS,
  WORKING_ACCESS_LEVELS,
  failedPasswordRules,
} from '../../shared/index.js';

import type { ManagedUserDetail } from '../../api/endpoints.js';
import { userApi } from '../../api/endpoints.js';
import { HelpHint } from '../../components/help-hint.js';
import { PasswordRules } from '../../components/password-rules.js';
import { Select } from '../../components/select.js';
import { Button, Field } from '../../components/ui.js';

interface AccessDraft {
  readonly departmentId: string;
  readonly level: AccessLevel;
}

interface Draft {
  readonly firstName: string;
  readonly lastName: string;
  readonly jobRoleId: string;
  readonly homeDepartmentId: string;
  readonly overlays: readonly AccessOverlay[];
  readonly extraAccess: readonly AccessDraft[];
  readonly isAssignable: boolean;
}

const EMPTY_DRAFT: Draft = {
  firstName: '',
  lastName: '',
  jobRoleId: '',
  homeDepartmentId: '',
  overlays: [],
  extraAccess: [],
  isAssignable: true,
};

interface Catalogues {
  readonly departments: readonly { readonly id: string; readonly name: string }[];
  readonly jobRoles: readonly {
    readonly id: string;
    readonly name: string;
    readonly departmentId: string | null;
  }[];
}

const FIRST_GRANT: AccessLevel = 'read';

function AccessRows({
  rows,
  departments,
  homeDepartmentId,
  onChange,
}: {
  readonly rows: readonly AccessDraft[];
  readonly departments: Catalogues['departments'];
  readonly homeDepartmentId: string;
  readonly onChange: (next: readonly AccessDraft[]) => void;
}): ReactNode {
  const available = departments.filter(
    (entry) => entry.id !== homeDepartmentId && !rows.some((row) => row.departmentId === entry.id),
  );

  return (
    <div className="flex flex-col gap-2">
      {rows.length === 0 ? (
        <p className="text-[12.5px] text-muted">
          Nothing extra. They can still file requests into every department.
        </p>
      ) : null}

      {rows.map((row, index) => (
        <div key={row.departmentId} className="flex flex-wrap items-center gap-2">
          <span className="min-w-[120px] flex-1 text-[13px] font-semibold text-navy dark:text-dark-ink">
            {departments.find((entry) => entry.id === row.departmentId)?.name ?? 'Unknown'}
          </span>

          <Select
            label="Access level"
            value={row.level}
            onChange={(next) => {
              onChange(
                rows.map((entry, at) =>
                  at === index ? { ...entry, level: next as AccessLevel } : entry,
                ),
              );
            }}
            options={WORKING_ACCESS_LEVELS.map((level) => ({
              value: level,
              label: ACCESS_LEVEL_LABELS[level],
            }))}
          />

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              onChange(rows.filter((_, at) => at !== index));
            }}
          >
            Remove
          </Button>
        </div>
      ))}

      {available.length === 0 ? null : (
        <Select
          label="Give access to another department"
          value=""
          onChange={(next) => {
            if (next !== '') onChange([...rows, { departmentId: next, level: FIRST_GRANT }]);
          }}
          options={[
            { value: '', label: 'Give access to another department…' },
            ...available.map((entry) => ({ value: entry.id, label: entry.name })),
          ]}
        />
      )}
    </div>
  );
}

function SectionLabel({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="mt-1 flex items-center gap-1.5">
      <span className="eyebrow text-muted">{title}</span>
      <HelpHint title={title}>{children}</HelpHint>
    </div>
  );
}

const POSITION_HINT = (
  <>
    <p>
      Their job title. Positions belong to departments, so the list below offers the ones that
      exist inside the department chosen above.
    </p>
    <p>
      It decides what they may <strong className="font-semibold text-navy dark:text-dark-ink">
      raise</strong> anywhere in the company — purchase requests, hire requests. It says nothing
      about what they can see; that is the department.
    </p>
    <p>
      A position from another department can still be held — a Technical Recruiter placed in the
      IT Division is a real arrangement, not a mistake — and the form says so rather than
      silently swapping it.
    </p>
  </>
);

function firstPositionIn(
  jobRoles: Catalogues['jobRoles'],
  departmentId: string,
): string {
  if (departmentId === '') return '';
  return jobRoles.find((role) => role.departmentId === departmentId)?.id ?? '';
}

function PositionPicker({
  jobRoleId,
  homeDepartmentId,
  jobRoles,
  departments,
  onChange,
}: {
  readonly jobRoleId: string;
  readonly homeDepartmentId: string;
  readonly jobRoles: Catalogues['jobRoles'];
  readonly departments: Catalogues['departments'];
  readonly onChange: (next: string) => void;
}): ReactNode {
  const inDepartment = jobRoles.filter(
    (role) => homeDepartmentId !== '' && role.departmentId === homeDepartmentId,
  );

  const current = jobRoles.find((role) => role.id === jobRoleId);
  const outsider =
    current !== undefined && !inDepartment.some((role) => role.id === current.id) ? current : null;

  const departmentName = (id: string | null): string =>
    id === null ? 'no department' : (departments.find((entry) => entry.id === id)?.name ?? 'unknown');

  return (
    <>
      <Select
        label="Position"
        value={jobRoleId}
        onChange={onChange}
        options={[
          { value: '', label: homeDepartmentId === '' ? 'Choose a department first…' : 'Choose a position…' },
          ...inDepartment.map((role) => ({ value: role.id, label: role.name })),
          ...(outsider === null
            ? []
            : [
                {
                  value: outsider.id,
                  label: `${outsider.name} — ${departmentName(outsider.departmentId)}`,
                },
              ]),
        ]}
      />

      {outsider !== null ? (
        <p className="-mt-1 text-[12.5px] text-muted">
          Their position sits in{' '}
          <span className="font-semibold">{departmentName(outsider.departmentId)}</span>, not the
          department chosen above. Kept as it is — pick another only if that is wrong.
        </p>
      ) : homeDepartmentId !== '' && inDepartment.length === 0 ? (
        <p className="-mt-1 text-[12.5px] text-caution">
          No position is placed in that department yet. Add one on the Positions screen.
        </p>
      ) : null}
    </>
  );
}

const HOME_DEPARTMENT_HINT = (
  <>
    <p>The department they actually work in. One per person.</p>
    <p>
      There they get full access automatically: they see the queue, work on its requests, and can
      be picked as the assignee.
    </p>
  </>
);

const DEPARTMENT_ACCESS_HINT = (
  <>
    <p>
      <strong className="font-semibold text-navy dark:text-dark-ink">
        Everyone can already file a request into every department.
      </strong>{' '}
      That is the default and needs no setting, so it is not listed here.
    </p>
    <p>Use this only when somebody needs more than that outside their home department:</p>
    <ul className="ml-4 list-disc space-y-1">
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Can view queue</strong> —
        sees that department's requests, changes nothing.
      </li>
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Can work on tickets</strong>{' '}
        — moves them and comments on them.
      </li>
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Full access</strong> — the
        same as a member of that department, including being made the assignee.
      </li>
    </ul>
    <p>
      <strong className="font-semibold text-navy dark:text-dark-ink">Remove</strong> takes the
      extra access away. They keep the default and can still file requests there.
    </p>
  </>
);

const OVERLAYS_HINT = (
  <>
    <p>
      Company-wide roles, on top of everything above — and they fall into two quite different
      kinds, which is why they are drawn in two groups.
    </p>
    <ul className="ml-4 list-disc space-y-1">
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Admin</strong> — sees and
        changes everything: every department, every board, these settings. Ignores workflow rules,
        so they can move any request anywhere.
      </li>
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">CEO</strong> — the same
        reach across requests and boards, without the settings screens.
      </li>
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Department Head</strong> —
        grants no extra access at all. It only means: email me about what happens in the
        departments where I have full access.
      </li>
    </ul>
  </>
);

const ASSIGNABLE_HINT = (
  <>
    <p>
      Whether they appear in the assignee list. Turn it off for people who raise requests but
      never carry them out — and for anyone on long leave, so work is not handed to them by
      mistake.
    </p>
    <p>
      <strong className="font-semibold text-navy dark:text-dark-ink">It is one of two
      conditions.</strong>{' '}
      Being the assignee of a request also needs{' '}
      <strong className="font-semibold text-navy dark:text-dark-ink">full</strong> access to the
      department that request sits in — their home department, or one raised to full above. The
      line underneath says where that currently lands.
    </p>
  </>
);

const POWER_OVERLAYS: readonly AccessOverlay[] = ACCESS_OVERLAYS.filter(
  (overlay) => overlay !== 'department_head',
);

const OVERLAY_NOTES: Readonly<Record<AccessOverlay, string>> = Object.freeze({
  admin: 'Everything, everywhere, including these settings.',
  ceo: 'The same reach over requests and boards, without the settings screens.',
  department_head: 'Copied on new requests in the departments where they have full access.',
});

function OverlayPicker({
  overlays,
  disabled,
  onChange,
}: {
  readonly overlays: readonly AccessOverlay[];
  readonly disabled?: AccessOverlay;
  readonly onChange: (next: readonly AccessOverlay[]) => void;
}): ReactNode {
  const box = (overlay: AccessOverlay): ReactNode => (
    <label
      key={overlay}
      className="flex items-start gap-2 text-[13px] text-navy dark:text-dark-ink"
    >
      <input
        type="checkbox"
        className="mt-0.5"
        checked={overlays.includes(overlay)}
        disabled={disabled === overlay}
        onChange={(event) => {
          onChange(
            event.target.checked
              ? [...overlays, overlay]
              : overlays.filter((entry) => entry !== overlay),
          );
        }}
      />
      <span>
        {ACCESS_OVERLAY_LABELS[overlay]}
        <span className="block text-[12px] text-muted">{OVERLAY_NOTES[overlay]}</span>
      </span>
    </label>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-semibold tracking-[0.04em] text-muted uppercase">
          Elevated access
        </span>
        {POWER_OVERLAYS.map(box)}
      </div>

      <div className="flex flex-col gap-2 border-t border-hairline pt-3 dark:border-dark-line">
        <span className="text-[12px] font-semibold tracking-[0.04em] text-muted uppercase">
          Notifications only
        </span>
        {box('department_head')}
      </div>
    </div>
  );
}

function AssignableSummary({
  isAssignable,
  homeDepartmentId,
  extraAccess,
  departments,
}: {
  readonly isAssignable: boolean;
  readonly homeDepartmentId: string;
  readonly extraAccess: readonly AccessDraft[];
  readonly departments: Catalogues['departments'];
}): ReactNode {
  if (!isAssignable) {
    return (
      <p className="text-[12.5px] text-muted">
        They cannot be made the assignee of anything while this is off.
      </p>
    );
  }

  const names = [
    ...(homeDepartmentId === ''
      ? []
      : [departments.find((entry) => entry.id === homeDepartmentId)?.name ?? 'Unknown']),
    ...extraAccess
      .filter((row) => row.level === 'full')
      .map((row) => departments.find((entry) => entry.id === row.departmentId)?.name ?? 'Unknown'),
  ];

  if (names.length === 0) {
    return (
      <p className="text-[12.5px] text-muted">
        Nowhere yet — being an assignee also needs full access to a department. Choose a home
        department above.
      </p>
    );
  }

  return (
    <p className="text-[12.5px] text-muted">
      Can be assigned in <span className="font-semibold">{names.join(', ')}</span> — the
      departments where they have full access.
    </p>
  );
}

function Panel({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 z-30 bg-navy-deep/40 backdrop-blur-sm lg:hidden"
      />

      <aside className="fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-hairline bg-white shadow-menu sm:max-w-[520px] dark:border-dark-line dark:bg-dark-panel">
        <header className="flex h-topbar shrink-0 items-center gap-3 border-b border-hairline px-5 dark:border-dark-line">
          <h2 className="font-display text-[16px] font-extrabold tracking-[-0.02em] text-navy dark:text-dark-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto rounded-field px-2 py-1 text-[18px] leading-none text-muted hover:bg-navy/5 hover:text-navy dark:hover:bg-white/5"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </aside>
    </>
  );
}

export function CreatePerson({
  departments,
  jobRoles,
  onClose,
  onCreated,
  onFail,
}: Catalogues & {
  readonly onClose: () => void;
  readonly onCreated: (person: ManagedUserDetail) => Promise<void>;
  readonly onFail: (error: unknown) => void;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  const create = useMutation({
    mutationFn: () =>
      userApi.create({
        email: email.trim(),
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        password,
        jobRoleId: draft.jobRoleId,
        homeDepartmentId: draft.homeDepartmentId,
        overlays: [...draft.overlays],
        extraAccess: draft.extraAccess.map((row) => ({ ...row })),
        isAssignable: draft.isAssignable,
      }),
    onSuccess: onCreated,
    onError: onFail,
  });

  const ready =
    email.trim() !== '' &&
    failedPasswordRules(password).length === 0 &&
    draft.firstName.trim() !== '' &&
    draft.lastName.trim() !== '' &&
    draft.jobRoleId !== '' &&
    draft.homeDepartmentId !== '';

  return (
    <Panel title="Add person" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />

        <div className="flex gap-3">
          <Field
            label="First name"
            value={draft.firstName}
            onChange={(event) => {
              setDraft({ ...draft, firstName: event.target.value });
            }}
          />
          <Field
            label="Last name"
            value={draft.lastName}
            onChange={(event) => {
              setDraft({ ...draft, lastName: event.target.value });
            }}
          />
        </div>

        <Field
          label="Temporary password"
          type="password"
          value={password}
          hint="They are asked to change it the first time they sign in."
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />

        <PasswordRules value={password} className="-mt-1" />

        <SectionLabel title="Home department">{HOME_DEPARTMENT_HINT}</SectionLabel>
        <Select
          label="Home department"
          value={draft.homeDepartmentId}
          onChange={(next) => {
            setDraft({
              ...draft,
              homeDepartmentId: next,
              jobRoleId: firstPositionIn(jobRoles, next),
              extraAccess: draft.extraAccess.filter((row) => row.departmentId !== next),
            });
          }}
          options={[
            { value: '', label: 'Choose a department…' },
            ...departments.map((entry) => ({ value: entry.id, label: entry.name })),
          ]}
        />

        <SectionLabel title="Position">{POSITION_HINT}</SectionLabel>
        <PositionPicker
          jobRoleId={draft.jobRoleId}
          homeDepartmentId={draft.homeDepartmentId}
          jobRoles={jobRoles}
          departments={departments}
          onChange={(next) => {
            setDraft({ ...draft, jobRoleId: next });
          }}
        />

        <SectionLabel title="Access to other departments">{DEPARTMENT_ACCESS_HINT}</SectionLabel>
        <AccessRows
          rows={draft.extraAccess}
          departments={departments}
          homeDepartmentId={draft.homeDepartmentId}
          onChange={(next) => {
            setDraft({ ...draft, extraAccess: next });
          }}
        />

        <SectionLabel title="Company-wide roles">{OVERLAYS_HINT}</SectionLabel>
        <OverlayPicker
          overlays={draft.overlays}
          onChange={(next) => {
            setDraft({ ...draft, overlays: next });
          }}
        />

        <div className="mt-1 flex items-center gap-1.5">
          <label className="flex items-center gap-2 text-[13px] text-navy dark:text-dark-ink">
            <input
              type="checkbox"
              checked={draft.isAssignable}
              onChange={(event) => {
                setDraft({ ...draft, isAssignable: event.target.checked });
              }}
            />
            Can be given work
          </label>
          <HelpHint title="Can be given work">{ASSIGNABLE_HINT}</HelpHint>
        </div>

        <AssignableSummary
          isAssignable={draft.isAssignable}
          homeDepartmentId={draft.homeDepartmentId}
          extraAccess={draft.extraAccess}
          departments={departments}
        />

        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            loading={create.isPending}
            disabled={!ready}
            onClick={() => {
              create.mutate();
            }}
          >
            Create account
          </Button>
        </div>
      </div>
    </Panel>
  );
}

export function EditPerson({
  person,
  departments,
  jobRoles,
  isSelf,
  onClose,
  onDone,
  onFail,
}: Catalogues & {
  readonly person: ManagedUserDetail;
  readonly isSelf: boolean;
  readonly onClose: () => void;
  readonly onDone: (message: string, updated: ManagedUserDetail | null) => Promise<void>;
  readonly onFail: (fallback: string) => (error: unknown) => void;
}): ReactNode {
  const [draft, setDraft] = useState<Draft>({
    firstName: person.firstName,
    lastName: person.lastName,
    jobRoleId: person.jobRoleId,
    homeDepartmentId: person.homeDepartmentId ?? '',
    overlays: person.overlays,
    extraAccess: person.access
      .filter((entry) => entry.departmentId !== person.homeDepartmentId && entry.level !== 'create')
      .map((entry) => ({ departmentId: entry.departmentId, level: entry.level })),
    isAssignable: person.isAssignable,
  });

  const [password, setPassword] = useState('');

  const save = useMutation({
    mutationFn: () =>
      userApi.update(person.id, {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        jobRoleId: draft.jobRoleId,
        ...(draft.homeDepartmentId === '' ? {} : { homeDepartmentId: draft.homeDepartmentId }),
        overlays: [...draft.overlays],
        extraAccess: draft.extraAccess.map((row) => ({ ...row })),
        isAssignable: draft.isAssignable,
      }),
    onSuccess: (updated) => onDone('Saved.', updated),
    onError: onFail('That change could not be saved.'),
  });

  const setActive = useMutation({
    mutationFn: (isActive: boolean) => userApi.update(person.id, { isActive }),
    onSuccess: (updated) =>
      onDone(updated.isActive ? 'Account reactivated.' : 'Account deactivated.', updated),
    onError: onFail('That account could not be changed.'),
  });

  const resetPassword = useMutation({
    mutationFn: () =>
      userApi.resetPassword(person.id, {
        newPassword: password,
        requireChangeOnNextLogin: true,
      }),
    onSuccess: async () => {
      setPassword('');
      await onDone('Password reset. They must change it at the next sign-in.', null);
    },
    onError: onFail('That password could not be set.'),
  });

  return (
    <Panel title={`${person.firstName} ${person.lastName}`} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <p className="text-[12.5px] text-muted">{person.email}</p>

        <div className="flex gap-3">
          <Field
            label="First name"
            value={draft.firstName}
            onChange={(event) => {
              setDraft({ ...draft, firstName: event.target.value });
            }}
          />
          <Field
            label="Last name"
            value={draft.lastName}
            onChange={(event) => {
              setDraft({ ...draft, lastName: event.target.value });
            }}
          />
        </div>

        <SectionLabel title="Home department">{HOME_DEPARTMENT_HINT}</SectionLabel>
        <Select
          label="Home department"
          value={draft.homeDepartmentId}
          onChange={(next) => {
            setDraft({
              ...draft,
              homeDepartmentId: next,
              jobRoleId: firstPositionIn(jobRoles, next),
              extraAccess: draft.extraAccess.filter((row) => row.departmentId !== next),
            });
          }}
          options={departments.map((entry) => ({ value: entry.id, label: entry.name }))}
        />

        <SectionLabel title="Position">{POSITION_HINT}</SectionLabel>
        <PositionPicker
          jobRoleId={draft.jobRoleId}
          homeDepartmentId={draft.homeDepartmentId}
          jobRoles={jobRoles}
          departments={departments}
          onChange={(next) => {
            setDraft({ ...draft, jobRoleId: next });
          }}
        />

        <SectionLabel title="Access to other departments">{DEPARTMENT_ACCESS_HINT}</SectionLabel>
        <AccessRows
          rows={draft.extraAccess}
          departments={departments}
          homeDepartmentId={draft.homeDepartmentId}
          onChange={(next) => {
            setDraft({ ...draft, extraAccess: next });
          }}
        />

        <SectionLabel title="Company-wide roles">{OVERLAYS_HINT}</SectionLabel>
        <OverlayPicker
          overlays={draft.overlays}
          {...(isSelf ? { disabled: 'admin' as const } : {})}
          onChange={(next) => {
            setDraft({ ...draft, overlays: next });
          }}
        />

        <div className="mt-1 flex items-center gap-1.5">
          <label className="flex items-center gap-2 text-[13px] text-navy dark:text-dark-ink">
            <input
              type="checkbox"
              checked={draft.isAssignable}
              onChange={(event) => {
                setDraft({ ...draft, isAssignable: event.target.checked });
              }}
            />
            Can be given work
          </label>
          <HelpHint title="Can be given work">{ASSIGNABLE_HINT}</HelpHint>
        </div>

        <AssignableSummary
          isAssignable={draft.isAssignable}
          homeDepartmentId={draft.homeDepartmentId}
          extraAccess={draft.extraAccess}
          departments={departments}
        />

        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            loading={save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >
            Save
          </Button>
        </div>

        <section className="mt-4 border-t border-hairline pt-4 dark:border-dark-line">
          <h3 className="text-[13px] font-bold text-navy dark:text-dark-ink">Set a new password</h3>
          <p className="mt-1 mb-2 text-[12.5px] leading-relaxed text-muted">
            For someone locked out. They are asked to change it the moment they sign in.
          </p>

          <PasswordRules value={password} />

          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <Field
                label="New password"
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="mb-4"
              loading={resetPassword.isPending}
              disabled={failedPasswordRules(password).length > 0}
              onClick={() => {
                resetPassword.mutate();
              }}
            >
              Set
            </Button>
          </div>
        </section>

        <section className="mt-4 border-t border-hairline pt-4 dark:border-dark-line">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="text-[13px] font-bold text-navy dark:text-dark-ink">
                {person.isActive ? 'Deactivate this account' : 'Reactivate this account'}
              </h3>
              <p className="mt-1 text-[12.5px] leading-relaxed text-muted">
                {person.isActive
                  ? 'They can no longer sign in or be given new work. Requests they already hold stay where they are — check those before deactivating.'
                  : 'They can sign in again and be given work.'}
              </p>
            </div>

            <Button
              type="button"
              variant={person.isActive ? 'danger' : 'secondary'}
              size="sm"
              loading={setActive.isPending}
              disabled={isSelf}
              onClick={() => {
                setActive.mutate(!person.isActive);
              }}
            >
              {person.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>

          {isSelf ? (
            <p className="mt-2 text-[12px] text-muted">You cannot deactivate your own account.</p>
          ) : null}
        </section>
      </div>
    </Panel>
  );
}
